import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { CurrencyInput, Field, Select } from '@payaca/ambrosia';

import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import React, { FC } from 'react';
import { z } from 'zod/v4';

export const USER_AND_COST_SCHEMA = z.object({
  userId: z.string().min(1, 'User is required'),
  costPerHour: z.number().min(0),
});

type UserAndCost = z.infer<typeof USER_AND_COST_SCHEMA>;

export type TProps = {
  userAndCost: UserAndCost;
  onChange: (userAndCost: UserAndCost) => void;
};

const UserAndCostFieldset: FC<TProps> = (props) => {
  const { userAndCost, onChange } = props;

  const { data: regionPreferences } = useGetMyRegionPreferences();
  const { data: activeUsers } = useGetMyAccountUsersExcludedDeactivated();

  const handleUserChange = (newUserId: string) => {
    const user = activeUsers?.find((u) => u.id === newUserId);

    onChange({
      ...userAndCost,
      userId: newUserId,
      costPerHour: user?.costPerHour?.value || 0,
    });
  };

  return (
    <>
      {/* User */}
      <PermissionGuard
        renderIfHasPermissions={[
          TimelogsPermissions.CREATE_TIMELOGS,
          TimelogsPermissions.UPDATE_TIMELOGS,
        ]}
      >
        <Field name="userAndCost.userId">
          <Field.Label>User</Field.Label>
          <Select
            value={userAndCost.userId}
            onChange={handleUserChange}
            multiple={false}
            options={
              activeUsers?.map((u) => ({
                label: u.fullName,
                value: u.id,
              })) || []
            }
            placeholder="Select a user"
          />
        </Field>
      </PermissionGuard>

      {/* Cost per hour */}
      <PermissionGuard
        renderIfHasPermissions={[TimelogsPermissions.SET_TIMELOG_COST_PER_HOUR]}
      >
        <Field name="userAndCost.costPerHour">
          <Field.Label>Cost per hour</Field.Label>
          <CurrencyInput
            value={userAndCost.costPerHour}
            onChange={(value) =>
              onChange({
                ...userAndCost,
                costPerHour: value,
              })
            }
            currency={regionPreferences?.currency}
          >
            <CurrencyInput.Suffix>per hour</CurrencyInput.Suffix>
          </CurrencyInput>
        </Field>
      </PermissionGuard>
    </>
  );
};

export default UserAndCostFieldset;
